/** Centre-crops a picked photo to a square and shrinks it for upload (JPEG data URL). */
export async function squarePhoto(file: File, size = 256, quality = 0.85, type = 'image/jpeg'): Promise<string> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
    const url = canvas.toDataURL(type, quality);
    // Browsers that can't encode the asked type return PNG, which can outgrow the upload limit.
    return url.startsWith(`data:${type};`) || url.length < 300_000 ? url : canvas.toDataURL('image/jpeg', quality);
  } finally {
    bitmap.close();
  }
}

/** Square logo that keeps transparency where the browser can encode WebP. */
export const squareLogo = (file: File) => squarePhoto(file, 256, 0.9, 'image/webp');

/** A brand logo shrunk to fit (never cropped: logos are often wide), keeping transparency as WebP. */
export async function fittedLogo(file: File, maxWidth = 480, maxHeight = 240, quality = 0.9): Promise<string> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    const scale = Math.min(1, maxWidth / bitmap.width, maxHeight / bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas not supported');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const url = canvas.toDataURL('image/webp', quality);
    return url.startsWith('data:image/webp;') || url.length < 300_000 ? url : canvas.toDataURL('image/png');
  } finally {
    bitmap.close();
  }
}
